import type { EnvName } from "@lms/shared-types";
import { z } from "zod";

// Zod probes `Function("")` once to decide whether it may compile validators; the portal's CSP is
// `default-src 'self'` with no 'unsafe-eval', so the probe is refused and Chrome logs a CSP error on
// every page load (seen on dev, 2026-09-19). Zod handles the refusal; the console noise it does not.
z.config({ jitless: true });

/**
 * Runtime configuration, written to /config.json by the portal stack so a
 * single build serves every environment and every tenant host. The SPA
 * picks its Cognito app client by the host it was served on (design.md
 * §6). `productName` is the build-time default the shell shows until the
 * tenant's own value arrives with /v1/me (ADR-0008 item 2). The mock flag
 * is a build-time switch (`VITE_MOCK=1`): local development and the e2e
 * use fixtures and a fake sign-in.
 */
export const ConfigSchema = z.object({
  envName: z.enum(["dev", "staging", "prod", "local", "mock"]),
  productName: z.string().default("Learn"),
  apiBaseUrl: z.string().default("/api"),
  cognito: z
    .object({
      issuer: z.string().url(),
      /** The managed-login host: `auth.<zone>`, or the prefix domain while that provisions. */
      domain: z.string(),
      /** App client id per host the SPA may be served on. */
      clients: z.record(z.string(), z.string()),
    })
    .optional(),
  /** SHA-256 of each email domain that signs in through its own identity provider (the config-file SSO model). */
  ssoDomainHashes: z.array(z.string()).default([]),
});
export type RuntimeConfig = z.infer<typeof ConfigSchema> & { envName: EnvName | "local" | "mock" };

export const isMock = import.meta.env.VITE_MOCK === "1";

export const DEFAULT_CONFIG: RuntimeConfig = { envName: "local", productName: "Learn", apiBaseUrl: "/api", ssoDomainHashes: [] };

let cached: RuntimeConfig | undefined;

export async function loadConfig(): Promise<RuntimeConfig> {
  if (cached) return cached;
  if (isMock) {
    cached = { envName: "mock", productName: "Learn", apiBaseUrl: "/api", cognito: { issuer: "https://mock.invalid", domain: "mock.invalid", clients: {} }, ssoDomainHashes: [] };
    return cached;
  }
  try {
    const res = await fetch("/config.json", { cache: "no-cache" });
    if (!res.ok) return (cached = DEFAULT_CONFIG);
    cached = ConfigSchema.parse(await res.json()) as RuntimeConfig;
  } catch {
    cached = DEFAULT_CONFIG;
  }
  return cached;
}

/** For tests. */
export function resetConfig(): void {
  cached = undefined;
}

export const LMS_VERSION: string = import.meta.env.VITE_LMS_VERSION ?? "dev";

/** `v0.1.0` — the same string the footer shows and the release-notes page anchors on. */
export const vtag = (version: string): string => (version.startsWith("v") ? version : `v${version}`);

/** The app client for the host this page was served on; undefined when this host serves no tenant. */
export function clientIdForHost(config: RuntimeConfig, host = typeof window === "undefined" ? "" : window.location.host): string | undefined {
  return config.cognito?.clients[host] ?? config.cognito?.clients[host.replace(/:\d+$/, "")];
}
